/* eslint-disable react/prop-types */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router";
import { t } from "ttag";

import cx from "classnames";
import MetabaseSettings from "metabase/lib/settings";
import { isSyncCompleted, isSyncInProgress } from "metabase/lib/syncing";

import ModalWithTrigger from "metabase/components/ModalWithTrigger";
import LoadingSpinner from "metabase/components/LoadingSpinner";
import FormMessage from "metabase/components/form/FormMessage";
import Modal from "metabase/components/Modal";
import SyncingModal from "metabase/containers/SyncingModal";

import DeleteDatabaseModal from "../components/DeleteDatabaseModal";
import { TableCellContent, TableCellSpinner } from "./DatabaseListApp.styled";

import Database from "metabase/entities/databases";

import {
  getDeletes,
  getDeletionError,
  getIsAddingSampleDatabase,
  getAddSampleDatabaseError,
} from "../selectors";
import {
  deleteDatabase,
  addSampleDatabase,
  closeSyncingModal,
} from "../database";

const RELOAD_INTERVAL = 2000;

const getReloadInterval = (state, props, databases = []) => {
  return databases.some(d => isSyncInProgress(d)) ? RELOAD_INTERVAL : 0;
};

const mapStateToProps = (state, props) => ({
  hasSampleDatabase: Database.selectors.getHasSampleDatabase(state),
  isAddingSampleDatabase: getIsAddingSampleDatabase(state),
  addSampleDatabaseError: getAddSampleDatabaseError(state),

  created: props.location.query.created,
  engines: MetabaseSettings.get("engines"),
  showSyncingModal: MetabaseSettings.get("show-database-syncing-modal"),

  deletes: getDeletes(state),
  deletionError: getDeletionError(state),
});

const mapDispatchToProps = {
  // NOTE: still uses deleteDatabase from metabaseadmin/databases/databases.js
  // rather than metabase/entities/databases since it updates deletes/deletionError
  deleteDatabase: deleteDatabase,
  addSampleDatabase: addSampleDatabase,
  closeSyncingModal,
};

@Database.loadList({
  reloadInterval: getReloadInterval,
})
@connect(mapStateToProps, mapDispatchToProps)
export default class DatabaseList extends Component {
  constructor(props) {
    super(props);

    props.databases.map(database => {
      this["deleteDatabaseModal_" + database.id] = React.createRef();
    });

    this.state = {
      isSyncingModalOpened: (props.created && props.showSyncingModal) || false,
    };
  }

  componentDidMount() {
    if (this.state.isSyncingModalOpened) {
      this.props.closeSyncingModal();
    }
  }

  onSyncingModalClose = () => {
    this.setState({ isSyncingModalOpened: false });
  };

  static propTypes = {
    databases: PropTypes.array,
    hasSampleDatabase: PropTypes.bool,
    engines: PropTypes.object,
    deletes: PropTypes.array,
    deletionError: PropTypes.object,
    created: PropTypes.string,
    showSyncingModal: PropTypes.bool,
    closeSyncingModal: PropTypes.func,
  };

  render() {
    const {
      databases,
      hasSampleDatabase,
      isAddingSampleDatabase,
      addSampleDatabaseError,
      engines,
      deletionError,
    } = this.props;
    const { isSyncingModalOpened } = this.state;

    const error = deletionError || addSampleDatabaseError;

    return (
      <div className="wrapper">
        <section className="PageHeader px2 clearfix">
          <Link
            to="/admin/databases/create"
            className="Button Button--primary float-right"
          >{t`Add database`}</Link>
          <h2 className="PageTitle">{t`Databases`}</h2>
        </section>
        {error && (
          <section>
            <FormMessage formError={error} />
          </section>
        )}
        <section>
          <table className="ContentTable">
            <thead>
              <tr>
                <th>{t`Name`}</th>
                <th>{t`Engine`}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {databases ? (
                [
                  databases.map(database => {
                    const isDeleting =
                      this.props.deletes.indexOf(database.id) !== -1;
                    return (
                      <tr
                        key={database.id}
                        className={cx({ disabled: isDeleting })}
                      >
                        <td>
                          <TableCellContent>
                            {!isSyncCompleted(database) && (
                              <TableCellSpinner size={16} borderWidth={2} />
                            )}
                            <Link
                              to={"/admin/databases/" + database.id}
                              className="text-bold link"
                            >
                              {database.name}
                            </Link>
                          </TableCellContent>
                        </td>
                        <td>
                          {engines && engines[database.engine]
                            ? engines[database.engine]["driver-name"]
                            : database.engine}
                        </td>
                        {isDeleting ? (
                          <td className="text-right">{t`Deleting...`}</td>
                        ) : (
                          <td className="Table-actions">
                            <ModalWithTrigger
                              ref={this["deleteDatabaseModal_" + database.id]}
                              triggerClasses="Button Button--danger"
                              triggerElement={t`Delete`}
                            >
                              <DeleteDatabaseModal
                                database={database}
                                onClose={() =>
                                  this[
                                    "deleteDatabaseModal_" + database.id
                                  ].current.close()
                                }
                                onDelete={() =>
                                  this.props.deleteDatabase(database.id)
                                }
                              />
                            </ModalWithTrigger>
                          </td>
                        )}
                      </tr>
                    );
                  }),
                ]
              ) : (
                <tr>
                  <td colSpan={4}>
                    <LoadingSpinner />
                    <h3>{t`Loading ...`}</h3>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {!hasSampleDatabase ? (
            <div className="pt4">
              <span
                className={cx("p2 text-italic", {
                  "border-top": databases && databases.length > 0,
                })}
              >
                {isAddingSampleDatabase ? (
                  <span className="text-light no-decoration">
                    {t`Restoring the sample database...`}
                  </span>
                ) : (
                  <a
                    className="text-light text-brand-hover no-decoration"
                    onClick={() => this.props.addSampleDatabase()}
                  >
                    {t`Bring the sample database back`}
                  </a>
                )}
              </span>
            </div>
          ) : null}
        </section>
        <Modal
          small
          isOpen={isSyncingModalOpened}
          onClose={this.onSyncingModalClose}
        >
          <SyncingModal onClose={this.onSyncingModalClose} />
        </Modal>
      </div>
    );
  }
}
